import React, { useState } from 'react';
import { Button, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import DropzoneArea from '../DropzoneArea';

const RivhitCsvImportModal = ({
    open,
    onClose,
    previewRivhitCsv,
    commitRivhitCsv,
    createMissingPalCode,
    direction,
    t,
    palCodeCategoryOptions = [],
    palCodes = [],
    Constants = {},
    user
}) => {
    const customStyles = {
        container: (provided) => ({
            ...provided,
            width: '100%',
            boxSizing: 'border-box !important'
        }),
        control: (provided) => ({
            ...provided,
            minHeight: '40px',
            width: '100% !important',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: 'none',
            boxShadow: 'none',
            padding: '0 10px',
            textAlign: 'left',
            boxSizing: 'border-box !important',
            border: '1px solid transparent',
            '&:focus-within': {
                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black'
        }),
        singleValue: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black',
        }),
        menu: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? '#F9AA2A'
                : state.isFocused
                    ? '#ebebeb'
                    : null,
            color: 'black',
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: state.isFocused ? 'darkgray' : 'black',
            '&:hover': {
                color: 'darkgray',
            },
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 2002,
        }),
    };

    const [rivhitCsvFile, setRivhitCsvFile] = useState(null);
    const [rivhitCsvPreviewDone, setRivhitCsvPreviewDone] = useState(false);
    const [rivhitCsvProcessing, setRivhitCsvProcessing] = useState(false);
    const [rivhitCsvParsedRows, setRivhitCsvParsedRows] = useState(0);
    const [rivhitCsvAccountCards, setRivhitCsvAccountCards] = useState([]);
    const [rivhitCsvMissingPalCodes, setRivhitCsvMissingPalCodes] = useState([]);
    const [editingPalCode, setEditingPalCode] = useState(null);
    const [bulkMode, setBulkMode] = useState(false);
    const [selectedPalCodesForBulk, setSelectedPalCodesForBulk] = useState([]);
    const [palCodeForm, setPalCodeForm] = useState({
        palCodeId: '',
        palCodeType: '',
        palCodeCategory: null
    });
    const [bulkForm, setBulkForm] = useState({
        palCodeType: '',
        palCodeCategory: null
    });

    // Format PalCodeType options
    const palCodeTypeOptions = (Constants.PalCodeType || []).map(type => ({
        label: type,
        value: type
    }));

    // Format PalCodeCategory options
    const palCodeCategorySelectOptions = palCodeCategoryOptions.map(cat => ({
        label: cat.palCodeCategoryId,
        value: cat.palCodeCategoryId
    }));

    // Calculate next orderNumber for a given category
    const calculateNextOrderNumber = (category) => {
        if (!category || palCodes.length === 0) return 100;

        const palCodesInCategory = palCodes.filter(
            pc => pc.palCodeCategory === category
        );

        const maxOrderNumber = palCodesInCategory.reduce((max, pc) => {
            const orderNum = pc.orderNumber || 0;
            return Math.max(max, orderNum);
        }, 0);

        return maxOrderNumber > 0 ? maxOrderNumber + 100 : 100;
    };

    const handleRivhitCsvDrop = files => {
        if (files?.length) setRivhitCsvFile(files[0]);
    };

    const handleRivhitCsvPreview = async () => {
        if (!rivhitCsvFile) return;

        try {
            setRivhitCsvProcessing(true);
            const res = await previewRivhitCsv(rivhitCsvFile);
            const accountCards = res?.accountCards ?? [];
            const missingPalCodes = res?.missingPalCodes ?? [];
            const parsedRows = res?.parsedRows ?? 0;

            setRivhitCsvAccountCards(accountCards);
            setRivhitCsvMissingPalCodes(missingPalCodes);
            setRivhitCsvParsedRows(parsedRows);
            setRivhitCsvPreviewDone(true);
        } catch (err) {
            console.error('Rivhit CSV preview failed:', err);
            setRivhitCsvPreviewDone(false);
        } finally {
            setRivhitCsvProcessing(false);
        }
    };

    const handleCreatePalCode = async (palCode) => {
        const palCodeName = typeof palCode === 'string' ? palCode : palCode.palCodeName;

        if (!palCodeForm.palCodeId || !palCodeForm.palCodeType) {
            return;
        }

        try {
            setRivhitCsvProcessing(true);

            // Calculate orderNumber in background
            const calculatedOrderNumber = palCodeForm.palCodeCategory
                ? calculateNextOrderNumber(palCodeForm.palCodeCategory)
                : 100;

            const newPalCode = {
                palCodeId: palCodeForm.palCodeId,
                palCodeType: palCodeForm.palCodeType,
                palCodeCategory: palCodeForm.palCodeCategory,
                orderNumber: calculatedOrderNumber,
                accountantId: user.accountantId,
            };

            const response = await createMissingPalCode(newPalCode);
            if (!response.ok) {
                const errorText = await response.text();
                console.error("PalCode creation failed:", errorText);
                throw new Error(errorText);
            }

            // Remove from missing list
            setRivhitCsvMissingPalCodes(prev =>
                prev.filter(pc => pc.palCodeName !== palCodeName)
            );

            // Reset form
            setEditingPalCode(null);
            setPalCodeForm({
                palCodeId: '',
                palCodeType: '',
                palCodeCategory: null
            });
        } catch (err) {
            console.error('PalCode creation error:', err);
            throw err;
        } finally {
            setRivhitCsvProcessing(false);
        }
    };

    const handleBulkCreatePalCodes = async () => {
        if (!bulkForm.palCodeType || selectedPalCodesForBulk.length === 0) {
            return;
        }

        try {
            setRivhitCsvProcessing(true);

            // Calculate base orderNumber
            const baseOrderNumber = bulkForm.palCodeCategory
                ? calculateNextOrderNumber(bulkForm.palCodeCategory)
                : 100;

            // Create all selected PalCodes
            const createPromises = selectedPalCodesForBulk.map((palCode, index) => {
                const palCodeName = typeof palCode === 'string' ? palCode : palCode.palCodeName;
                const palCodeId = typeof palCode === 'object' ? palCode.palCodeId : palCodeName;
                
                const newPalCode = {
                    palCodeId: palCodeId,
                    palCodeType: bulkForm.palCodeType,
                    palCodeCategory: bulkForm.palCodeCategory,
                    orderNumber: baseOrderNumber + (index * 100),
                    accountantId: user.accountantId,
                };
                return createMissingPalCode(newPalCode);
            });

            const responses = await Promise.all(createPromises);
            const failed = responses.filter(r => !r.ok);

            if (failed.length > 0) {
                console.error("Some PalCodes failed to create");
                throw new Error(`${failed.length} PalCodes failed to create`);
            }

            // Remove created PalCodes from missing list
            setRivhitCsvMissingPalCodes(prev =>
                prev.filter(pc => !selectedPalCodesForBulk.some(selected => {
                    const selectedName = typeof selected === 'string' ? selected : selected.palCodeName;
                    const pcName = typeof pc === 'string' ? pc : pc.palCodeName;
                    return selectedName === pcName;
                }))
            );

            // Reset bulk mode
            setBulkMode(false);
            setSelectedPalCodesForBulk([]);
            setBulkForm({
                palCodeType: '',
                palCodeCategory: null
            });
        } catch (err) {
            console.error('Bulk PalCode creation error:', err);
            throw err;
        } finally {
            setRivhitCsvProcessing(false);
        }
    };

    const handleRivhitCsvCommit = async () => {
        if (!rivhitCsvFile) return;

        try {
            setRivhitCsvProcessing(true);

            const res = await commitRivhitCsv(rivhitCsvFile);

            if (res && res.errorMessages && res.errorMessages.length > 0) {
                console.error('Import errors:', res.errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Rivhit CSV commit error:', err);
        } finally {
            setRivhitCsvProcessing(false);
        }
    };

    const handleClose = () => {
        setRivhitCsvFile(null);
        setRivhitCsvPreviewDone(false);
        setRivhitCsvAccountCards([]);
        setRivhitCsvMissingPalCodes([]);
        setRivhitCsvParsedRows(0);
        setEditingPalCode(null);
        setBulkMode(false);
        setSelectedPalCodesForBulk([]);
        setPalCodeForm({
            palCodeId: '',
            palCodeType: '',
            palCodeCategory: null
        });
        setBulkForm({
            palCodeType: '',
            palCodeCategory: null
        });
        onClose();
    };

    const startEditingPalCode = (palCode) => {
        const palCodeName = typeof palCode === 'string' ? palCode : palCode.palCodeName;
        const palCodeId = typeof palCode === 'object' ? palCode.palCodeId : palCodeName;
        setEditingPalCode(palCodeName);
        setPalCodeForm({
            palCodeId: palCodeId,
            palCodeType: '',
            palCodeCategory: null
        });
    };

    if (!open) return null;

    return (
        <div className="download-modal-overlay" onClick={handleClose} style={{ zIndex: 2000 }}>
            <div className="download-modal" onClick={e => e.stopPropagation()} style={{ zIndex: 2001 }}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: rivhitCsvPreviewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">ריווחית CSV - {t('ImportAccountCards')}</h4>
                        <IconButton
                            className="client-back-icon"
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div>
                        <h5>{t('UploadFile')} (.csv)</h5>
                        <DropzoneArea
                            key={rivhitCsvFile ? rivhitCsvFile.name : 'rivhit-csv-empty'}
                            onDrop={handleRivhitCsvDrop}
                            accept=".csv"
                            filesLimit={1}
                        />
                        {rivhitCsvFile && (
                            <div style={{ marginTop: 8 }}>
                                <span>{t('FileSelected')}: {rivhitCsvFile.name}</span>
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button
                            variant="contained"
                            onClick={handleRivhitCsvPreview}
                            disabled={!rivhitCsvFile || rivhitCsvProcessing}
                        >
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!rivhitCsvPreviewDone || rivhitCsvProcessing || rivhitCsvMissingPalCodes.length > 0}
                            onClick={handleRivhitCsvCommit}
                        >
                            {rivhitCsvProcessing ? t('Processing') : t('Import')}
                        </Button>
                    </div>

                    {rivhitCsvPreviewDone && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div style={{ marginBottom: 15 }}>
                                <p style={{ fontSize: '14px', color: '#666' }}>
                                    {t('ParsedRows') || 'שורות שנקראו'}: <strong>{rivhitCsvParsedRows}</strong> |
                                    {' '}{t('AccountCards') || 'כרטיסי חשבון'}: <strong>{rivhitCsvAccountCards.length}</strong>
                                </p>
                            </div>

                            {/* Missing PalCodes Section */}
                            {rivhitCsvMissingPalCodes.length > 0 && (
                                <div style={{ marginBottom: 25, padding: 15, backgroundColor: '#fff3cd', borderRadius: 5, border: '1px solid #ffc107' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                                        <div>
                                            <h5 style={{ color: '#856404', margin: 0 }}>
                                                {t('MissingPalCodes') || 'חשבונות ראשיים חסרים'}
                                            </h5>
                                            <p style={{ fontSize: '14px', color: '#856404', margin: '5px 0 0 0' }}>
                                                {t('MissingPalCodesDescription') || 'יש ליצור את החשבונות הראשיים הבאים לפני הייבוא:'}
                                            </p>
                                        </div>
                                        <Button
                                            variant={bulkMode ? "contained" : "outlined"}
                                            size="small"
                                            onClick={() => {
                                                setBulkMode(!bulkMode);
                                                setSelectedPalCodesForBulk([]);
                                                if (!bulkMode) {
                                                    setBulkForm({
                                                        palCodeType: '',
                                                        palCodeCategory: null
                                                    });
                                                }
                                            }}
                                        >
                                            {bulkMode ? (t('CancelBulk') || 'ביטול יצירה מרובה') : (t('BulkCreate') || 'יצירה מרובה')}
                                        </Button>
                                    </div>

                                    {/* Bulk Creation Form */}
                                    {bulkMode && (
                                        <div style={{ marginBottom: 20, padding: 15, backgroundColor: '#f0f0f0', borderRadius: 5 }}>
                                            <h6 style={{ marginBottom: 10 }}>{t('BulkCreatePalCodes') || 'יצירת חשבונות ראשיים מרובים'}</h6>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                <div style={{ marginBottom: 8 }}>
                                                    <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                        {t('PalCodeType') || 'סוג חשבון ראשי'} <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Select
                                                        placeholder={t('Select') || 'בחר'}
                                                        options={palCodeTypeOptions}
                                                        value={palCodeTypeOptions.find(opt => opt.value === bulkForm.palCodeType) || null}
                                                        onChange={(selectedOption) => {
                                                            setBulkForm({ ...bulkForm, palCodeType: selectedOption?.value || '' });
                                                        }}
                                                        styles={customStyles}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                    />
                                                </div>
                                                <div style={{ marginBottom: 8 }}>
                                                    <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                        {t('PalCodeCategory') || 'קטגוריית חשבון ראשי'}
                                                    </label>
                                                    <Select
                                                        placeholder={t('None') || 'ללא'}
                                                        options={palCodeCategorySelectOptions}
                                                        value={palCodeCategorySelectOptions.find(opt => opt.value === bulkForm.palCodeCategory) || null}
                                                        onChange={(selectedOption) => {
                                                            setBulkForm({ ...bulkForm, palCodeCategory: selectedOption?.value || null });
                                                        }}
                                                        styles={customStyles}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                    />
                                                </div>
                                                <div style={{ marginTop: 10 }}>
                                                    <p style={{ fontSize: '12px', color: '#666', marginBottom: 5 }}>
                                                        {t('SelectedPalCodes') || 'נבחרו'}: {selectedPalCodesForBulk.length} / {rivhitCsvMissingPalCodes.length}
                                                    </p>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        onClick={handleBulkCreatePalCodes}
                                                        disabled={rivhitCsvProcessing || !bulkForm.palCodeType || selectedPalCodesForBulk.length === 0}
                                                        fullWidth
                                                    >
                                                        {t('CreateSelected') || `צור ${selectedPalCodesForBulk.length} חשבונות`}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {rivhitCsvMissingPalCodes.map((palCode, idx) => {
                                        const palCodeName = typeof palCode === 'string' ? palCode : palCode.palCodeName;
                                        const palCodeId = typeof palCode === 'object' ? palCode.palCodeId : palCodeName;
                                        
                                        return (
                                            <div key={idx} style={{ marginBottom: 15, padding: 10, backgroundColor: '#fff', borderRadius: 3 }}>
                                                {editingPalCode === palCodeName ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                        <TextField
                                                            label={t('PalCodeId') || 'מזהה חשבון ראשי'}
                                                            value={palCodeForm.palCodeId}
                                                            onChange={(e) => setPalCodeForm({ ...palCodeForm, palCodeId: e.target.value })}
                                                            size="small"
                                                            fullWidth
                                                            disabled
                                                        />
                                                        <div style={{ marginBottom: 8 }}>
                                                            <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                                {t('PalCodeType') || 'סוג חשבון ראשי'} <span style={{ color: 'red' }}>*</span>
                                                            </label>
                                                            <Select
                                                                placeholder={t('Select') || 'בחר'}
                                                                options={palCodeTypeOptions}
                                                                value={palCodeTypeOptions.find(opt => opt.value === palCodeForm.palCodeType) || null}
                                                                onChange={(selectedOption) => {
                                                                    setPalCodeForm({ ...palCodeForm, palCodeType: selectedOption?.value || '' });
                                                                }}
                                                                styles={customStyles}
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                            />
                                                        </div>
                                                        <div style={{ marginBottom: 8 }}>
                                                            <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                                {t('PalCodeCategory') || 'קטגוריית חשבון ראשי'}
                                                            </label>
                                                            <Select
                                                                placeholder={t('None') || 'ללא'}
                                                                options={palCodeCategorySelectOptions}
                                                                value={palCodeCategorySelectOptions.find(opt => opt.value === palCodeForm.palCodeCategory) || null}
                                                                onChange={(selectedOption) => {
                                                                    setPalCodeForm({ ...palCodeForm, palCodeCategory: selectedOption?.value || null });
                                                                }}
                                                                styles={customStyles}
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                            />
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 10 }}>
                                                            <Button
                                                                variant="contained"
                                                                color="primary"
                                                                size="small"
                                                                onClick={() => handleCreatePalCode(palCode)}
                                                                disabled={rivhitCsvProcessing || !palCodeForm.palCodeId || !palCodeForm.palCodeType}
                                                            >
                                                                {t('create') || 'צור'}
                                                            </Button>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => {
                                                                    setEditingPalCode(null);
                                                                    setPalCodeForm({
                                                                        palCodeId: '',
                                                                        palCodeType: '',
                                                                        palCodeCategory: null
                                                                    });
                                                                }}
                                                            >
                                                                {t('Cancel') || 'ביטול'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            {bulkMode && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedPalCodesForBulk.some(selected => {
                                                                        const selectedName = typeof selected === 'string' ? selected : selected.palCodeName;
                                                                        return selectedName === palCodeName;
                                                                    })}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedPalCodesForBulk(prev => [...prev, palCode]);
                                                                        } else {
                                                                            setSelectedPalCodesForBulk(prev => prev.filter(selected => {
                                                                                const selectedName = typeof selected === 'string' ? selected : selected.palCodeName;
                                                                                return selectedName !== palCodeName;
                                                                            }));
                                                                        }
                                                                    }}
                                                                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                                />
                                                            )}
                                                            <span style={{ fontWeight: 'bold' }}>{palCodeName}</span>
                                                        </div>
                                                        {!bulkMode && (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => startEditingPalCode(palCode)}
                                                            >
                                                                {t('CreatePalCode') || 'צור חשבון ראשי'}
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* No Missing PalCodes Message */}
                            {rivhitCsvMissingPalCodes.length === 0 && (
                                <div style={{ marginBottom: 25, padding: 15, backgroundColor: '#d4edda', borderRadius: 5, border: '1px solid #28a745' }}>
                                    <h5 style={{ color: '#155724', margin: '0 0 5px 0' }}>
                                        ✓ {t('NoMissingPalCodes') || 'אין חשבונות ראשיים חסרים'}
                                    </h5>
                                    <p style={{ fontSize: '14px', color: '#155724', margin: 0 }}>
                                        {t('ReadyToImport') || 'כל החשבונות הראשיים קיימים. לחץ על "יבוא" להמשך.'}
                                    </p>
                                </div>
                            )}

                            {/* Account Cards Table */}
                            {rivhitCsvAccountCards.length === 0 ? (
                                <p style={{ color: '#666' }}>{t('NoAccountCardsFound') || 'לא נמצאו כרטיסי חשבון'}</p>
                            ) : (
                                <>
                                    <h5>{t('AccountCards') || 'כרטיסי חשבון'}</h5>
                                    <table className="sortcode-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('CardId') || 'מזהה כרטיס'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('CardName') || 'שם הכרטיס'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('PalCode') || 'חשבון ראשי'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('AccountCaseId') || 'מס.ע.מ'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rivhitCsvAccountCards.map((entry, i) => {
                                                const isMissingPalCode = rivhitCsvMissingPalCodes.some(missing => {
                                                    const missingName = typeof missing === 'string' ? missing : missing.palCodeName;
                                                    return missingName === entry.palCode;
                                                });
                                                return (
                                                    <tr key={i} style={{
                                                        backgroundColor: i % 2 === 0 ? '#fff' : '#f9f9f9',
                                                        ...(isMissingPalCode ? { backgroundColor: '#fff3cd' } : {})
                                                    }}>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.cardId || ''}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.cardName || ''}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left', color: isMissingPalCode ? '#856404' : 'inherit' }}>
                                                            {entry.palCode || ''}
                                                            {isMissingPalCode && <span style={{ color: '#dc3545', marginLeft: 5 }}>*</span>}
                                                        </td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.accountCaseId || ''}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RivhitCsvImportModal;
